import {
  useAiChatAttached,
  useAiChatMessage,
} from '@euler/app/flows/ai-suite/chat/AiChatActions';
import {
  CardNotice,
  ChatCard,
} from '@euler/app/flows/ai-suite/components/ChatCard';
import {
  PrimaryButton,
  SecondaryButton,
} from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { kBiNewDashboardName } from '@euler/model/bi/answer';
import { BiDashboardChoiceWidgetPayload } from '@euler/model/bi/widgets';
import { CirclePlus, LayoutDashboard, List } from 'lucide-react-native';
import { memo, useState } from 'react';
import { View } from 'react-native';
import { useBiAddTap } from '../bi-visualization/functions/useBiAddTap';
import { ChoiceRow } from './ChoiceRow';
import { NewDashboardForm } from './NewDashboardForm';
import { OtherDashboards } from './OtherDashboards';

type ChoiceMode = 'suggestions' | 'new' | 'other';

/**
 * Where to add: the suggestions, best first, then **新建看板** (a name
 * prompt) and **其他看板…** (the full list). With one suggestion, under
 * the reply's "要加到「X」吗？", it offers 是 and 新建看板. It takes a tap
 * only while it is the latest question, once.
 */
export const DashboardChoiceCard = memo(
  ({ payload }: { payload: BiDashboardChoiceWidgetPayload }) => {
    const { rpx } = useResponsiveContext();
    const attached = useAiChatAttached();
    const { latest } = useAiChatMessage();
    const { pending, busy, add } = useBiAddTap(payload.draftIds);
    const [mode, setMode] = useState<ChoiceMode>('suggestions');
    const [chosen, setChosen] = useState<string | null>(null);
    const { candidates } = payload;
    const [only] = candidates.length === 1 ? candidates : [];
    const open = attached && latest && !chosen;
    const disabled = !open || busy || pending;
    const choose = (dashboardId: string, name: string) => {
      if (add({ dashboardId }, name)) setChosen(`加到「${name}」`);
    };
    const create = (name: string) => {
      const label = name || kBiNewDashboardName;
      if (add(name ? { newDashboardName: name } : {}))
        setChosen(`加到新看板「${label}」`);
    };
    const count = payload.draftIds.length;
    return (
      <ChatCard
        icon={LayoutDashboard}
        color={aiColors.amber}
        title="选择看板"
        subtitle={[
          only ? `「${only.name}」` : null,
          count > 1 ? `共 ${count} 张图表` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      >
        {chosen ? (
          <CardNotice text={`已选择：${chosen}`} />
        ) : mode === 'new' ? (
          <NewDashboardForm
            disabled={disabled}
            onCancel={() => setMode('suggestions')}
            onCreate={create}
          />
        ) : (
          <>
            {only ? (
              <View style={{ flexDirection: 'row', gap: rpx(8) }}>
                <View style={{ flex: 1 }}>
                  <PrimaryButton
                    title="是"
                    disabled={disabled}
                    onPress={() => choose(only.dashboardId, only.name)}
                  />
                </View>
                <SecondaryButton
                  title="新建看板"
                  icon={CirclePlus}
                  disabled={disabled}
                  onPress={() => setMode('new')}
                />
              </View>
            ) : (
              <>
                <View style={{ gap: rpx(8) }}>
                  {candidates.map((candidate, index) => (
                    <ChoiceRow
                      key={candidate.dashboardId}
                      name={candidate.name}
                      hint={index === 0 ? '最匹配' : undefined}
                      disabled={disabled}
                      onPress={() =>
                        choose(candidate.dashboardId, candidate.name)
                      }
                    />
                  ))}
                </View>
                {mode === 'other' ? (
                  <OtherDashboards
                    exclude={candidates.map(item => item.dashboardId)}
                    disabled={disabled}
                    onChoose={choose}
                  />
                ) : null}
                <View style={{ flexDirection: 'row', gap: rpx(8) }}>
                  <SecondaryButton
                    title="新建看板"
                    icon={CirclePlus}
                    disabled={disabled}
                    onPress={() => setMode('new')}
                  />
                  {mode === 'other' ? null : (
                    <SecondaryButton
                      title="其他看板…"
                      icon={List}
                      disabled={disabled}
                      onPress={() => setMode('other')}
                    />
                  )}
                </View>
              </>
            )}
          </>
        )}
      </ChatCard>
    );
  },
);
